import { Injectable } from "@angular/core";
import {
  Subject,
  catchError,
  from,
  map,
  Observable,
  switchMap,
  throwError,
  lastValueFrom,
  BehaviorSubject,
} from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { EncrDecrService } from "./encr-decr.service";
import { STATIC_KEYS } from "src/constants/constants";
import { ApiService } from "./api.service";
import { MasterDataApiService } from "src/pages/master-data/master-data-api.service";
import { ChipType } from "src/pages/org-approval/constant";
import { AbstractControl, FormControl } from "@angular/forms";
import { ApiResponse } from "src/models/api-response.model";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  clearStallSelection$ = new Subject<void>();

  private sideBarVisibleSubject = new BehaviorSubject<boolean>(true);
  sideBarVisible$ = this.sideBarVisibleSubject.asObservable();
  private downloadPNGSubject = new Subject<void>();
  downloadPNG$ = this.downloadPNGSubject.asObservable();

  constructor(
    private _translateService: TranslateService,
    private encDecService: EncrDecrService,
    private apiService: ApiService,
    private apiMasterService: MasterDataApiService
  ) {}

  $formControl(control: AbstractControl) {
    return <FormControl>control;
  }

  /**
   * Function to get access-token from localstorage
   */
  getAccessToken(key = STATIC_KEYS.IDP_AUTH_TOKEN): any {
    return this.getItem(key);
  }

  getChipType(actionType: string): ChipType {
    switch (actionType) {
      case "download":
      case "approved":
        return ChipType.success;
      case "viewed":
        return ChipType.gray;
      case "pending":
        return ChipType.warning;
      case "escalated":
      case "rejected":
        return ChipType.error;
      default:
        return ChipType.gray;
    }
  }

  /**
   * Function to set encrypted access-token in localstorage
   * @param val string or object
   */
  setItem(key: any, value: any, isEncrypt: boolean = false) {
    let valueToBeStored = JSON.stringify(value);
    if (isEncrypt) {
      valueToBeStored = this.encDecService.set(valueToBeStored);
    }
    localStorage.setItem(key, valueToBeStored);
  }

  /**
   * Function to get decrypted item from localstorage
   * @param key string
   */
  getItem(key: string, isEncrypt: boolean = false) {
    let storedItem = localStorage.getItem(key);
    if (!storedItem) {
      return null; // or return any other default value you prefer
    }
    if (isEncrypt) {
      storedItem = this.encDecService.get(storedItem);
    }
    try {
      return JSON.parse(storedItem as string);
    } catch (error) {
      console.error(`Error parsing JSON from ${key}:`, error);
      return null; // or return any other default value you prefer
    }
  }

  /**
   * Remove specific key from localStorage
   * @param key string
   */
  removeItem(key: string) {
    localStorage.removeItem(key);
  }

  /**
   * Function to clear LocalStorage
   */
  clearLocalStorage() {
    localStorage.clear();
  }

  setLang(event: any, val?: any) {
    if (val) {
      this.setItem("lang-type", val);
      this._translateService.use(val);
    } else {
      this.setItem("lang-type", event.value);
      this._translateService.use(event.value);
    }
  }

  getPermissions() {
    let permissions: any;
    try {
      permissions = JSON.parse(
        this.encDecService.get(localStorage.getItem("permissions")) || "[]"
      );
    } catch (error) {
      permissions = [];
    }
    return permissions;
  }

  validatePermissions(requiredPermissions: string[]): boolean {
    return true;
    // Get user's permissions from your CommonService
    const userPermissions = this.getPermissions() || [];
    // if it super admin
    if (
      userPermissions.includes("all") ||
      (requiredPermissions && requiredPermissions.length == 0)
    )
      return true;

    // Check if the user has at least one of the required permissions
    return requiredPermissions.some((permission) =>
      userPermissions.includes(permission)
    );
  }

  isWebView() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipod|ipad/.test(userAgent);

    if (ios) {
      return !/safari/.test(userAgent) || "standalone" in window.navigator;
    }

    return /wv|webview/.test(userAgent);
  }

  openLink(link?: any): void {
    window.open(link, "_blank");
  }

  onError(event: Event) {
    (event.target as HTMLImageElement).src = "assets/images/placeholder.jpg";
  }

  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  }

  uploadFileToServer(
    file: File,
    artifact_type?: string,
    isLoading: boolean = false
  ): Observable<{ type: string; url: string; key: string }> {
    if (!file) {
      return throwError(() => new Error("No file to upload"));
    }

    return from(this.fileToBase64(file)).pipe(
      switchMap((base64Data) =>
        this.apiService.uploadFileApi(
          {
            image_url: base64Data,
            file_name: file.name,
            type: "content",
            artifact_type,
          },
          isLoading
        )
      ),
      map((response) => ({
        type: file.type,
        url: response?.data?.url || "",
        key: response?.data?.key || "",
      })),
      catchError((error) => {
        return throwError(() => new Error(error));
      })
    );
  }

  getMasterTags(
    params?: Record<string, number | string>[],
    keySlug: string = "tags"
  ): Observable<{ data: any; header: any }> {
    return this.apiMasterService.getCodeMastersTagsV2();
  }

  async getAllUsersList(
    page: number,
    size: number = 50,
    search: string = "",
    roles?: string[]
  ): Promise<any> {
    try {
      const filter: any = {};

      if (search) {
        filter.search = search;
      }
      if (roles?.length) {
        filter.roles = roles;
      }

      const res = await lastValueFrom(
        this.apiService.getUserList({
          page,
          size,
          ...(Object.keys(filter).length ? { filter } : {}),
        })
      );

      const usersList = (res?.data?.list || []).filter((user: any) => {
        return !!user.username || !!user.email;
      });

      const options =
        usersList.map((user: any) => ({
          label: `${user.username} - ${user.email}`,
          value: user.id,
          username: user.username,
        })) || [];

      return {
        options,
        page,
        size,
        total: res?.data?.total || 0,
        totalPages: res?.data?.totalPages,
      };
    } catch (error) {
      console.error("Error fetching users:", error);
      return [];
    }
  }

  toggleSidebar(state: boolean) {
    this.sideBarVisibleSubject.next(state);
  }

  scrollIntoViewWithOffset(element: HTMLElement, offset = 120) {
    const offsetTop =
      element.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    });
  }

  downloadPNG() {
    this.downloadPNGSubject.next();
  }

  downloadBlob(blob: Blob, filename: string, successCB?: Function) {
    const downloadHelper = () => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      successCB?.();
    };

    const reader = new FileReader();

    reader.onerror = downloadHelper;

    reader.onload = () => {
      try {
        const result: ApiResponse<any> = JSON.parse(reader.result as string);
      } catch (error) {
        downloadHelper();
      }
    };

    reader.readAsText(blob);
  }
}
